import { useState, useRef, useEffect } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Checkbox } from "primereact/checkbox";
import { Toast } from "primereact/toast";
import LoginService from "../../service/LoginService";
import ReCaptchaButton from "../ReCaptchaButton/ReCaptchaButton";
import localeText from "../../locale";
import logoColor from "../../pages/images/logo-color-horizontal.png";

const LoginDialog = ({ visible, onHide, onLoginSuccess, onOpenRegister, fromCheckout = false }) => {
  const [rememberMe, setRememberMe] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [tokenRecaptcha, setTokenRecaptcha] = useState("");
  const [loading, setLoading] = useState(false);
  const [displayForgotPassword, setDisplayForgotPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [usernameForgotPass, setUsernameForgotPass] = useState("");

  const toast = useRef(null);
  const loginService = new LoginService();

  useEffect(() => {
    if (!visible) {
      setUsername("");
      setPassword("");
      setTokenRecaptcha("");
      setRememberMe(false);
    }
  }, [visible]);

  async function login(username, password) {
    if (username.length === 0 || password.length === 0) {
      toast.current.show({
        severity: "error",
        summary: localeText("error"),
        detail: localeText("pleaseFillOut"),
        life: 6000,
      });
      return;
    }

    if (tokenRecaptcha.length === 0) {
      toast.current.show({
        severity: "error",
        summary: localeText("error"),
        detail: localeText("pleaseReverify"),
        life: 6000,
      });
      return;
    }

    try {
      setLoading(true);
      const res = await loginService.postLogin(username, password);

      if (typeof res === "string" && res.trim() === "Too many login attempts") {
        // Bloqueio temporário: a senha nem foi conferida, então não é "credenciais incorretas"
        toast.current.show({
          severity: "error",
          summary: localeText("error"),
          detail: localeText("tooManyLoginAttempts"),
          life: 8000,
        });
        setLoading(false);
      } else if (res === "Bad credentials") {
        toast.current.show({
          severity: "error",
          summary: localeText("error"),
          detail: localeText("incorrectCredentials"),
          life: 6000,
        });
        setLoading(false);
      } else if (res === "User is disabled") {
        toast.current.show({
          severity: "error",
          summary: localeText("error"),
          detail: localeText("pleaseCheckEmail"),
          life: 6000,
        });
        setLoading(false);
      } else {
        localStorage.setItem("__extraconomy_token", res);

        try {
          const usernameResponse = await loginService.getUsername(res);

          let actualUsername = username;
          if (usernameResponse?.messageCode === 200001) {
            actualUsername = usernameResponse.result;
          } else if (usernameResponse?.result) {
            actualUsername = usernameResponse.result;
          } else if (usernameResponse?.username) {
            actualUsername = usernameResponse.username;
          }

          localStorage.setItem("__extraconomy_username", actualUsername);
        } catch (error) {
          console.error("Erro ao buscar username:", error);
          localStorage.setItem("__extraconomy_username", username);
        }

        if (
          localStorage.getItem("__extraconomy_lat__") &&
          localStorage.getItem("__extraconomy_ismobile__")
        ) {
          await loginService.postRegisterLocation(
            localStorage.getItem("__extraconomy_token"),
            localStorage.getItem("__extraconomy_lat__"),
            localStorage.getItem("__extraconomy_long__"),
            localStorage.getItem("__extraconomy_username"),
            localStorage.getItem("__extraconomy_precision__")
              ? localStorage.getItem("__extraconomy_precision__")
              : null,
          );
        }

        if (
          localStorage.getItem("__extraconomy_devicetoken__") &&
          localStorage.getItem("__extraconomy_ismobile__")
        ) {
          await loginService.postRegisterDevice(
            localStorage.getItem("__extraconomy_token"),
            localStorage.getItem("__extraconomy_devicetoken__"),
            localStorage.getItem("__extraconomy_platform__"),
            localStorage.getItem("__extraconomy_username"),
          );
        }

        if (
          localStorage.getItem("__extraconomy_token") &&
          JSON.parse(localStorage.getItem("__extraconomy_ismobile__"))
        ) {
          window.localStorage.setItem(
            "__extraconomy_token",
            localStorage.getItem("__extraconomy_token"),
          );
          window.localStorage.setItem(
            "__extraconomy_username",
            localStorage.getItem("__extraconomy_username"),
          );
          window.ReactNativeWebView.postMessage(
            JSON.stringify({
              token: localStorage.getItem("__extraconomy_token"),
              username: localStorage.getItem("__extraconomy_username"),
              isLogin: true,
            }),
          );
        }

        setLoading(false);
        if (onLoginSuccess) {
          onLoginSuccess(fromCheckout);
        }
      }
    } catch (e) {
      setLoading(false);
      console.log(e);
    }
  }

  async function sendEmail(email) {
    try {
      const res = await loginService.getForgotPassword(email);

      if (res.messageCode === 200000)
        toast.current.show({
          severity: "success",
          summary: localeText("success"),
          detail: localeText("pleaseCheckEmailTemp"),
        });
      else if (res.messageCode === 500012)
        toast.current.show({
          severity: "error",
          summary: localeText("error"),
          detail: localeText("userNotFound"),
        });
      else
        toast.current.show({
          severity: "error",
          summary: localeText("error"),
          detail: localeText("thisEmailDoesnt"),
        });

      setDisplayForgotPassword(false);
    } catch (e) {
      console.log(e);
    }
  }

  return (
    <>
      <Toast position="top-left" ref={toast} />

      <Dialog
        visible={displayForgotPassword}
        header={localeText("forgotPassword")}
        onHide={() => setDisplayForgotPassword(false)}
        breakpoints={{ "960px": "75vw" }}
        style={{ width: "50vw" }}
        draggable={false}
      >
        <div className="text-center mb-4">
          <ul className="list-none text-left">
            <li>
              <label htmlFor="email">
                {localeText("enterTheEmailAddressAssociatedYourAccount")}
              </label>
              <InputText
                value={email}
                name="email"
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                className="w-full mt-2"
              />
              <small className="text-xs text-gray-600">
                {localeText("weWillSendYouALinkToSecurelyCreateANewPassword")}
              </small>
            </li>

            <li className="mt-4">
              <Button
                label={localeText("sendEmail")}
                loading={loading}
                onClick={() => sendEmail(email)}
              />
            </li>
          </ul>
        </div>
      </Dialog>

      <Dialog
        visible={visible}
        onHide={onHide}
        dismissableMask={!loading}
        closable={!loading}
        draggable={false}
        breakpoints={{ "960px": "90vw", "640px": "95vw" }}
        style={{ width: "660px", maxWidth: "95vw", height: "770px" }}
        contentClassName="p-0"
        headerStyle={{ background: "#fff", paddingInline: "0.5rem" }}
        headerClassName="login-dialog-close-icon"
        closeIcon={
          <i
            className="pi pi-times"
            style={{
              color: "#B3BAC5",
              cursor: "pointer",
            }}
          />
        }
      >
        <div className="flex flex-column align-items-center gap-4 p-4 h-full">
          <img src={logoColor} alt="Extraconomy logo" style={{ height: "40px" }} />
          <div className="flex flex-column align-items-center gap-2 w-full">
            <div className="text-3xl font-bold" style={{ color: "#181D27" }}>
              {localeText("welcomeBack")}
            </div>
            <div className="text-center" style={{ color: "#535862" }}>
              {localeText("signInWith")}
            </div>
          </div>

          <div className="flex flex-column gap-3 w-full" style={{ maxWidth: "360px" }}>
            <div className="w-full">
              <label
                className="font-semibold text-base"
                htmlFor="username"
                style={{ color: "#0D0D0E" }}
              >
                {localeText("emailOrUsername")}
              </label>
              <div className="mt-2">
                <InputText
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  name="username"
                  className="w-full p-3"
                  placeholder={localeText("enterRegisteredEmail")}
                  disabled={loading}
                />
              </div>
            </div>

            <div className="w-full">
              <label
                className="font-semibold text-base"
                htmlFor="password"
                style={{ color: "#0D0D0E" }}
              >
                {localeText("password")}
              </label>
              <div className="mt-2">
                <InputText
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type="password"
                  name="password"
                  className="w-full p-3"
                  placeholder={localeText("enterYourPassword")}
                  disabled={loading}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      login(username, password);
                    }
                  }}
                />
              </div>
            </div>

            <div className="flex justify-content-between align-items-center">
              <div className="flex align-items-center gap-2">
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.checked)}
                  disabled={loading}
                />
                <label htmlFor="rememberMe" className="text-sm" style={{ color: "#344563" }}>
                  {localeText("rememberMe")}
                </label>
              </div>
              <Button
                className="p-0 p-no-outline text-sm"
                style={{ color: "#344563" }}
                link
                onClick={() => setDisplayForgotPassword(true)}
                disabled={loading}
              >
                {localeText("forgotPassword")}
              </Button>
            </div>

            <ReCaptchaButton token={tokenRecaptcha} setToken={setTokenRecaptcha} />

            <div>
              <Button
                label={localeText("accessMyAccount")}
                loading={loading}
                onClick={() => login(username, password)}
                style={{ backgroundColor: "#28AB69" }}
                className="w-full font-semibold text-base text-white p-no-outline border-none px-4 py-3 border-round-lg"
              />
            </div>
          </div>

          <div className="text-sm text-center" style={{ color: "#344563" }}>
            {localeText("notPartOfNetwork")}{" "}
            <span
              className="font-semibold cursor-pointer"
              style={{ color: "#245AE5" }}
              onClick={() => {
                if (!loading && onOpenRegister) {
                  onOpenRegister();
                }
              }}
            >
              {localeText("createAccountNow")}
            </span>
          </div>

          <div className="text-center text-sm mt-auto" style={{ color: "#7A869A" }}>
            © Extraconomy - 2025
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default LoginDialog;
